import { useAppDispatch, useAppSelector } from 'app/hooks';
import type { EnrichedAccessory } from 'components/layout/modals/compatible-combinations-browser-modal';
import type {
  ExistingSystemComponentType,
  SelectedSystemType
} from 'data/types';
import { beginCompatibilityAdds, endCompatibilityAdds } from 'features/systems';
import { Dispatch, SetStateAction, useState } from 'react';
import type { AccessoryPicks } from 'utils/ducted-accessory-groups';
import { selectDuctedPairLoads } from 'utils/ducted-pair-context';
import {
  addCombinationsToQuotable,
  applyAccessoryPicks,
  ComboNeedingSelection,
  findCombosNeedingSelection,
  rebuildCombinationAccessories,
  SelectedCombination
} from 'utils/quotable-add-pipeline';

type UseQuotableAddParams = {
  instanceUniqueId: string | null;
  components: ExistingSystemComponentType[];
  // The existing system `components` were read from; stamped on each
  // manufactured system so the quote's synthetic ducted layout can find that
  // system's placed equipment positions. Undefined when no system is selected.
  existingSystemUniqueId?: string;
  accessoryData: Record<string, EnrichedAccessory>;
  // The GUI-resolved tonnage seed (AHA-6796 ducted coverage): required — not
  // optional — so TypeScript forces every consumer to decide, and each
  // resolves it via the shared resolveTonnageSeed on the same Redux inputs
  // as the CCB-check dispatchers (the componentsKey rationale). Forwarded
  // verbatim into the Phase 6 POST.
  tonnageSeed: { min: number; max: number } | null;
  // Caller-specific success side-effects (the CCB modal clears its selection
  // and closes; the ducted panel adds the systems to the comparison). Only
  // fires when every group landed — errors stay in the pipeline's errorModal.
  onSuccess: (added: SelectedSystemType[]) => void;
  // Fires when a started add finishes, including failures. Absent while the
  // accessory picker is still open — that path has not started the pipeline.
  onRunSettled?: () => void;
};

// The accessory-choice state machine moved out of the CCB modal (AHA-6681
// CS-4): `pendingCombinations` is the snapshot taken when the caller begins
// an add — it isolates the add-flow from later table-toggling — and
// `combosNeedingSelection` is the subset (with multi-option groups) shown in
// the picker. `accessorySelection` feeds AccessorySelectionModal as-is.
export default function useQuotableAdd({
  instanceUniqueId,
  components,
  existingSystemUniqueId,
  accessoryData,
  tonnageSeed,
  onSuccess,
  onRunSettled
}: UseQuotableAddParams): {
  beginAdd: (
    combos: SelectedCombination[],
    accessoryPicks?: AccessoryPicks
  ) => void;
  accessorySelection: {
    openState: [boolean, Dispatch<SetStateAction<boolean>>];
    combinationsWithChoices: ComboNeedingSelection[];
    systemCount: number;
    onConfirm: (
      selections: Map<number, Array<{ accessoryId: number; typeId: number }>>
    ) => void;
  };
} {
  const dispatch = useAppDispatch();
  // Hydrated 'sizingBands' setting (AHA-6796): snapshotted onto each built
  // ducted zone by the pipeline so quoted match labels reflect the bands the
  // quote was filtered with. null → nothing attached, legacy labels.
  const sizingBands = useAppSelector((state) => state.data.sizingBands);
  // Read here rather than pushed through every caller. These are the same
  // block loads the Choose Systems fetches send for this existing system —
  // its System Design pair's when one is linked, else the project's (the
  // structure they were persisted on says which calculator produced them) —
  // and the tonnageSeed above is the same seed, which is what makes the
  // server resolve an identical load for a saved system and for the card it
  // was added from. Zeroes here are honest: no sizing calc ran, and the seed
  // carries the request.
  const pairLoads = useAppSelector((state) =>
    selectDuctedPairLoads(state, existingSystemUniqueId)
  );
  const calculatedLoads = {
    heating: pairLoads.heating,
    cooling: pairLoads.cooling
  };
  const [accessorySelectionOpen, setAccessorySelectionOpen] = useState(false);
  const [pendingCombinations, setPendingCombinations] = useState<
    SelectedCombination[]
  >([]);
  const [combosNeedingSelection, setCombosNeedingSelection] = useState<
    ComboNeedingSelection[]
  >([]);

  const runAdd = (combos: SelectedCombination[]) => {
    const combinationIds = combos.map((combo) => combo.combinationId);
    dispatch(beginCompatibilityAdds(combinationIds));
    return addCombinationsToQuotable({
      combos,
      instanceUniqueId,
      components,
      existingSystemUniqueId,
      tonnageSeed,
      calculatedLoads,
      sizingBands,
      dispatch,
      onSuccess
    }).finally(() => {
      dispatch(endCompatibilityAdds(combinationIds));
      onRunSettled?.();
    });
  };

  // Entry point for the caller's add affordance. When no picks were supplied
  // and any combination has multi-option accessory types, defer to the
  // AccessorySelectionModal; otherwise resolve every accessory type to one
  // offered option (the pick or the default) before the pipeline.
  const beginAdd = (
    selected: SelectedCombination[],
    accessoryPicks?: AccessoryPicks
  ) => {
    if (selected.length === 0) return;

    const needsSelection = accessoryPicks
      ? []
      : findCombosNeedingSelection(selected, accessoryData);

    if (needsSelection.length > 0) {
      setPendingCombinations(selected);
      setCombosNeedingSelection(needsSelection);
      setAccessorySelectionOpen(true);
      return;
    }

    runAdd(applyAccessoryPicks(selected, accessoryData, accessoryPicks ?? {}));
  };

  // Called by AccessorySelectionModal once the user has picked one accessory
  // per multi-option type: rebuild each affected combination's accessories
  // string, then hand off to the Phase 5/6 flow.
  const handleAccessorySelectionConfirm = (
    selections: Map<number, Array<{ accessoryId: number; typeId: number }>>
  ) => {
    const rebuilt = rebuildCombinationAccessories(
      pendingCombinations,
      selections,
      accessoryData
    );

    setAccessorySelectionOpen(false);
    runAdd(rebuilt);
  };

  return {
    beginAdd,
    accessorySelection: {
      openState: [accessorySelectionOpen, setAccessorySelectionOpen],
      combinationsWithChoices: combosNeedingSelection,
      systemCount: pendingCombinations.length,
      onConfirm: handleAccessorySelectionConfirm
    }
  };
}
